import React, { useState, useEffect } from 'react';
import { useAuth, RoleLabels } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Phone, Radio, Save, Shield, Mail, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = () => {
    const { token, user, refreshUser } = useAuth();
    const [profileData, setProfileData] = useState({ name: '', phone: '' });
    const [loading, setLoading] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        if (user) {
            setProfileData({ name: user.name || '', phone: user.phone || '' });
        }
    }, [user]);

    const handleSave = async () => {
        setLoading(true);
        try {
            await axios.put(`${API}/users/me/profile`, profileData, { headers });
            toast.success('Profil mis a jour');
            if (refreshUser) refreshUser();
        } catch (error) {
            toast.error('Erreur lors de la mise a jour');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 max-w-2xl" data-testid="profile-page">
            <div className="page-header">
                <h1 className="text-3xl font-bold text-white">Mon Profil</h1>
                <p className="page-subtitle">Informations personnelles et de contact</p>
            </div>

            {/* Read-only info */}
            <Card className="content-card">
                <CardHeader className="card-header">
                    <CardTitle className="card-title flex items-center gap-2">
                        <Shield className="w-5 h-5 text-blue-500" /> Informations systeme
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                            <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1"><Mail className="w-3 h-3" /> Email</p>
                            <p className="text-white text-sm font-mono">{user?.email}</p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                            <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1"><Briefcase className="w-3 h-3" /> Fonction</p>
                            <p className="text-white text-sm font-medium">{RoleLabels[user?.role] || user?.role}</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                            <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1"><Radio className="w-3 h-3" /> Canal radio</p>
                            <p className="text-white text-sm">{user?.canal_radio || <span className="text-zinc-600 italic">Non attribue</span>}</p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded border border-zinc-700">
                            <p className="text-xs text-zinc-500 mb-1 flex items-center gap-1"><Briefcase className="w-3 h-3" /> Position terrain</p>
                            <p className="text-white text-sm">{user?.position || <span className="text-zinc-600 italic">Non attribuee</span>}</p>
                        </div>
                    </div>
                    <p className="text-xs text-zinc-500">La fonction, le canal radio et la position sont attribues par un administrateur.</p>
                </CardContent>
            </Card>

            {/* Editable profile */}
            <Card className="content-card">
                <CardHeader className="card-header">
                    <CardTitle className="card-title flex items-center gap-2">
                        <User className="w-5 h-5 text-green-500" /> Coordonnees
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="form-group">
                        <Label className="form-label flex items-center gap-2"><User className="w-4 h-4" /> Nom complet</Label>
                        <Input value={profileData.name} onChange={(e) => setProfileData({...profileData, name: e.target.value})} className="tactical-input" data-testid="profile-name" />
                    </div>
                    <div className="form-group">
                        <Label className="form-label flex items-center gap-2"><Phone className="w-4 h-4" /> Telephone</Label>
                        <Input value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} className="tactical-input" placeholder="06 XX XX XX XX" data-testid="profile-phone" />
                    </div>
                    <div className="flex justify-end pt-2">
                        <Button onClick={handleSave} disabled={loading} className="tactical-btn" data-testid="profile-save-btn">
                            <Save className="w-4 h-4 mr-2" />
                            {loading ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default ProfilePage;
